import React, {
  useEffect,
  useState,
  useContext,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { Box, Grid, Typography } from "@mui/material";
import {
  ErrorButton,
  PrimaryButton,
  // OutlinedPrimaryButton,
} from "../../../../components/ui/Button";
import { useForm, Controller } from "react-hook-form";
import { useDispatch } from "react-redux";
import { PaymentTermLoanContext } from "../../../../hooks/context";
import { InputField } from "../../../../components/ui/Inputs";
import { PositiveNumberValidation } from "../../../../utils/helpers";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
// import WestIcon from "@mui/icons-material/West";
// import { clearPartPaymentType } from "../../../../services/redux/slices/documentFormSlice";

export interface RowData {
  [key: string]: string | number | null;
}

// Pre-Payment Charge configuration (Fixed ROI LANs only)
// const PRE_PAYMENT_RATE = 0.02; // 2% of the total amount entered
// const GST_RATE = 0.18; // 18% GST applied on top of the pre-payment charge
// const PRE_PAYMENT_DEBOUNCE_MS = 500;

const PartPaymentTermLoan = () => {
  const {
    formValue,
    handleFormValue,
    partPaymentData,
    setFormStep,
    applicableCharges,
    amountValue,
    setAmountValue,
    isXsOrSm,
    // dashboardData,
    // handleSelectPaymentType,
  }: any = useContext(PaymentTermLoanContext);

  // const dispatch = useDispatch();

  const [fieldError, setFieldError] = useState<string | null>("");
  const [netFieldError, setNetFieldError] = useState<string | null>("");
  // Pre-Payment Charges value (2% of total amount + 18% GST) for Fixed ROI LANs.
  // const [prePaymentCharges, setPrePaymentCharges] = useState<number>(
  //   Number(formValue?.prePaymentCharges) || 0
  // );

  // Debounce timer ref used while recomputing the pre-payment charge on amount change.
  // const prePaymentTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // const isFixedRoiLan: boolean = useMemo(() => {
  //   if(partPaymentData[0]?.repayBaseRate) {
  //     return false
  //   }
  //   return true;
  // }, [partPaymentData]);

  // const computePrePaymentCharges = useCallback(
  //   (totalAmount: number): number => {
  //     if (!isFixedRoiLan) return 0;
  //     if (!totalAmount || totalAmount <= 0) return 0;
  //     const baseCharge = totalAmount * PRE_PAYMENT_RATE;
  //     const withGst = baseCharge * (1 + GST_RATE);
  //     // Round to 2 decimals to avoid floating point noise.
  //     return Math.round(withGst * 100) / 100;
  //   },
  //   [isFixedRoiLan]
  // );

  useEffect(() => {
    // Scroll to the top of the page when the component mounts
    window.scrollTo({
      top: 0,
      behavior: "smooth", // Adds smooth scrolling
    });
  }, []);

  // Clean up the debounce timer on unmount.
  // useEffect(() => {
  //   return () => {
  //     if (prePaymentTimerRef.current) {
  //       clearTimeout(prePaymentTimerRef.current);
  //     }
  //   };
  // }, []);

  /**
   * Validates the amount entered by the user and updates the form value if valid.
   *
   * @param event - The event object containing the value entered by the user.
   *
   * The function performs the following checks:
   * - The amount should be less than or equal to three times the repayment amount plus 1770.
   * - The amount should be greater than or equal to half of the outstanding principal amount plus 1770.
   *
   * If the amount does not meet these criteria, an error message is set.
   * Otherwise, the form value is updated with the entered amount.
   */
  // const recomputePrePaymentCharges = useCallback(
  //   (totalAmount: number) => {
  //     const charges = computePrePaymentCharges(totalAmount);
  //     setPrePaymentCharges(charges);
  //     handleFormValue("prePaymentCharges", charges);
  //   },
  //   [computePrePaymentCharges, handleFormValue]
  // );

  const handleAmountValidation = (event: any) => {
    const amount: number = event.target.value;
    setAmountValue(amount);

    // Pre-Payment calculation runs on Total Amount onChange with debouncing.
    // if (prePaymentTimerRef.current) {
    //   clearTimeout(prePaymentTimerRef.current);
    // }
    // prePaymentTimerRef.current = setTimeout(() => {
    //   recomputePrePaymentCharges(Number(amount));
    // }, PRE_PAYMENT_DEBOUNCE_MS);

    const repayAmountValue: number = Math.floor(
      3 * (partPaymentData?.[0]?.newEmiAmount / 100) + applicableCharges
    );
    const outstandingPrincipalValue: number = Math.floor(
      0.5 * (partPaymentData?.[0]?.paymentOutstandingPrincipal / 100) +
        applicableCharges
    );

    if (amount > 0 && amount < repayAmountValue) {
      setFieldError(
        "Entered amount should be such that Net principal adjustment greater than or equal to 3 times the EMI value."
      );
    } else if (amount > 0 && amount > outstandingPrincipalValue) {
      setFieldError(
        "Entered amount should be such that net principal adjustment is less than or equal to 50 % of Principal Outstanding Amount"
      );
    } else {
      setNetFieldError("");
      setFieldError("");
      handleFormValue("amount", Number(amount));
    }
  };

  // const handleGoToPaymentSelection = () => {
  //   setFormStep(-1);
  //   dispatch(clearPartPaymentType());
  //   handleSelectPaymentType?.("");
  // };

  const handleValidateNetValue = () => {
    // const totalCharges = prePaymentCharges + applicableCharges;
    const netValue = Number(formValue?.amount) - applicableCharges;
    // Persist the computed net principal adjustment for downstream steps.
    // handleFormValue("netPrincipalAdjustment", netValue > 0 ? netValue : 0);
    if (netValue < 3 * (partPaymentData?.[0]?.newEmiAmount / 100)) {
      setNetFieldError(
        "Net Principal Adjustment must be greater than or equal to the sum of 3 EMIs."
      );
    } else {
      setNetFieldError(null);
      setFieldError("");
      setFormStep(1);
    }
  };

  /**
   * Calculates the net principle adjustment based on the form value amount and applicable charges.
   *
   * @returns {number} The net principle adjustment. If the calculated value is less than 0, returns 0.
   *
   * Dependencies:
   * - `formValue?.amount`: The amount entered in the form.
   * - `applicableCharges`: The applicable charges to be deducted from the form value amount.
   */
  const handleShowNetPrincipleAdj = useMemo(() => {
    const total =
      amountValue == null ? Number(formValue?.amount) : Number(amountValue);
    // Net Principal Adjustment = Total Amount - (Pre-Payment Charges + Amort Updation Charges)
    // const value = total - (prePaymentCharges + applicableCharges);
    let value;
    if (amountValue == null) {
      value = Number(formValue?.amount) - applicableCharges;
    } else {
      value = Number(amountValue) - applicableCharges;
    }
    if (!value || value < 0) {
      return 0;
    }
    return value;
  }, [formValue, applicableCharges, amountValue]);

  return (
    <Grid item sm={12}>
      <form>
        <Box>
          <Typography className="section-sub-heading mb-10 mt-20">
            PAYMENT DETAILS
          </Typography>
        </Box>
        <Grid container item spacing={2} className="form-container">
          <Grid item xs={12} sm={4} md={4} lg={4}>
            <div>
              <label
                htmlFor="custom-autocomplete"
                className="disbursement-inside-page-lable-condtion-dark"
              >
                Total Amount
              </label>
            </div>
            <InputField
              fullWidth
              placeholder="Enter desired Pre-Payment amount"
              type="number"
              autoComplete="off"
              onChange={(event: any) => handleAmountValidation(event)}
              // error={!!fieldError}
              // helperText={fieldError}
              inputProps={{
                onKeyPress: PositiveNumberValidation, // Restrict negative input
                min: 0, // Optional, for numeric fields to set a minimum value
              }}
              defaultValue={formValue?.amount}
            />
          </Grid>
          {/* <Grid item xs={12} sm={3} md={3} lg={3}>
            <div>
              <label
                htmlFor="custom-autocomplete"
                className="disbursement-inside-page-lable-condtion-dark"
              >
                Pre-Payment Charges
              </label>
            </div>
            <InputField
              fullWidth
              placeholder="Pre-Payment charges"
              type="number"
              autoComplete="off"
              disabled
              value={prePaymentCharges}
            />
          </Grid> */}
          <Grid item xs={12} sm={4} md={4} lg={4}>
            <div>
              <label
                htmlFor="custom-autocomplete"
                className="disbursement-inside-page-lable-condtion-dark"
              >
                {/* Amort Updation Charges */}
                Applicable Charges
              </label>
            </div>
            <InputField
              fullWidth
              placeholder="Enter Applicable charges"
              type="number"
              autoComplete="off"
              disabled
              value={applicableCharges}
            />
          </Grid>
          <Grid item xs={12} sm={3} md={3} lg={3}>
            <div>
              <label
                htmlFor="custom-autocomplete"
                className="disbursement-inside-page-lable-condtion-dark"
              >
                Net Principal Adjustment
              </label>
            </div>
            <InputField
              fullWidth
              placeholder="Enter Net principle adjustment"
              type="number"
              autoComplete="off"
              disabled
              value={handleShowNetPrincipleAdj}
              // error={!!netFieldError}
              // helperText={netFieldError}
            />
          </Grid>
        </Grid>

        <Typography
          sx={{ color: "#d32f2f", fontSize: "13px", paddingTop: "10px" }}
        >
          {fieldError}
        </Typography>
        <Typography
          sx={{ color: "#d32f2f", fontSize: "13px", paddingTop: "10px" }}
        >
          {netFieldError}
        </Typography>

        <Grid
          container
          // justifyContent="center" // Center the buttons horizontally
          // alignItems="center"
          spacing={2} // Maintain spacing between items
          mt={1}
          mb={isXsOrSm ? 2 : 0}
        >
          {/* <Grid
            item
            xs={6}
            sm={6}
            md={6}
            lg={6}
            container
            justifyContent="left"
          >
            <OutlinedPrimaryButton
              width={isXsOrSm ? "112px" : "129px"}
              height={isXsOrSm ? "30px" : "42px"}
              fullWidth={true}
              onClick={handleGoToPaymentSelection}
            >
              <WestIcon sx={{ fontSize: "17px" }} /> Previous
            </OutlinedPrimaryButton>
          </Grid> */}
          <Grid
            item
            xs={6}
            sm={6}
            md={6}
            lg={6}
            container
            justifyContent="right"
          >
            <PrimaryButton
              // fullWidth={true}
              width={isXsOrSm ? "111px" : "127px"}
              height={isXsOrSm ? "30px" : "42px"}
              onClick={() => handleValidateNetValue()}
              disabled={!!fieldError || !!netFieldError || amountValue <= 0}
            >
              Proceed &nbsp; <ArrowForwardIcon sx={{ fontSize: "17px" }} />
            </PrimaryButton>
          </Grid>
        </Grid>
      </form>
    </Grid>
  );
};

export default PartPaymentTermLoan;
